import type { FC } from 'react';
import React, { useEffect } from 'react';

import { Box, Fade, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Trans } from 'react-i18next';

import { InfoIcon } from '@setup/svg-icons/svg-icons';

type MuiTooltipProps = {
  color?: 'light' | 'dark';
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  isArrow?: boolean;
  isUseCustomStatement?: boolean;
  isOpen?: boolean;
  title: string;
  isShowOnMobile?: boolean;
  isHideOnMobile?: boolean;
  children: JSX.Element;
  style?: any;
  tooltipWithIcon?: boolean;
};

export const MuiTooltip: FC<MuiTooltipProps> = ({
  color = 'dark',
  placement = 'top',
  isArrow = true,
  isShowOnMobile = false,
  isUseCustomStatement,
  isHideOnMobile,
  isOpen,
  title,
  children,
  style,
  tooltipWithIcon = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const math = useMediaQuery(theme.breakpoints.down('md'));
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  useEffect(() => {
    // TODO add the same id for scrollable block
    document.getElementById('network-user-scroll')?.addEventListener('scroll', handleClose);

    return () => {
      document.getElementById('network-user-scroll')?.removeEventListener('scroll', handleClose);
    };
  }, []);

  if (!title) return children;

  return (
    <Tooltip
      className="mui-tooltip"
      title={
        tooltipWithIcon ? (
          <Box display="flex" gap="8px">
            <Box
              sx={{
                svg: {
                  width: '16px',
                  height: '16px',
                  path: {
                    fill: theme.palette.case.contrast.white,
                  },
                },
              }}
            >
              <InfoIcon />
            </Box>
            <Trans
              defaults={title || ''}
              components={{
                green: (
                  <Typography
                    variant="t12r"
                    color={theme.palette.case.aquamarine.a300}
                    component="span"
                    fontWeight={700}
                  />
                ),
              }}
            />
          </Box>
        ) : (
          <Trans
            defaults={title}
            components={{
              green: (
                <Typography
                  variant="t12r"
                  color={theme.palette.case.aquamarine.a300}
                  component="span"
                  fontWeight={500}
                />
              ),
            }}
          />
        )
      }
      placement={placement}
      arrow={isArrow}
      open={isUseCustomStatement ? isOpen : math && isHideOnMobile ? false : open}
      onClose={handleClose}
      onOpen={handleOpen}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 500 }}
      enterTouchDelay={0}
      leaveTouchDelay={math || isShowOnMobile ? 1000 : 0}
      disableFocusListener={isUseCustomStatement}
      disableHoverListener={isUseCustomStatement}
      disableTouchListener={isUseCustomStatement}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor:
              color === 'light' ? theme.palette.case.neutral.n50 : theme.palette.case.neutral.n500,
            color: color === 'light' ? theme.palette.case.contrast.black : theme.palette.case.contrast.white,
            padding: '8px !important',
            fontSize: theme.typography.small.fontSize,
            fontWeight: theme.typography.small.fontWeight,
            lineHeight: theme.typography.small.lineHeight,
            boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.18)',
            whiteSpace: 'pre-wrap',
            '& .MuiTooltip-arrow': {
              color: color === 'light' ? theme.palette.case.neutral.n50 : theme.palette.case.neutral.n500,
            },
            ...style,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};
